import React, {CSSProperties, memo} from 'react';
import {ListChildComponentProps} from 'react-window';
import styled from 'styled-components';

import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {fontSizes} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {EmojiCategoriesEnum} from '../../../../services/emojis/emojiList';
import {EmojiDefinition, isEmojiCategoriesEnum} from '../../../../services/emojis/emojis';
import {SkinTonesEnum} from '../../../../services/emojis/emojiTypes';
import {Emoji} from '../../emoji/emoji';
import {categoryRenderers} from './emojiDropdownCommon';

/*
 * Constants.
 */

export const emojiPerRow = 10;
const emojiSize = 22;

/*
 * Props.
 */

export type EmojiDropdownListData = ReadonlyArray<EmojiCategoriesEnum | ReadonlyArray<EmojiDefinition>>;

interface EmojiDropdownListRowProps extends ListChildComponentProps {
  skinTone: SkinTonesEnum;
  listData: EmojiDropdownListData;
  onEmojiMouseEnter: (definition: EmojiDefinition) => void;
  onEmojiMouseLeave: () => void;
  onEmojiClick: (definition: EmojiDefinition) => void;
}

/*
 * Style.
 */

const StyledRowDiv = styled.div`
  display: grid;
  grid-template-columns: repeat(${emojiPerRow}, 1fr);
  align-items: start;
`;

const StyledTitleDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.medium};
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledEmojiDiv = styled(ChromelessButton)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  border-radius: 4px;
  padding: 5px;

  &:hover {
    background-color: ${(p) => p.theme.alphas.gray10};
  }
`;

/*
 * Component.
 */

export const EmojiDropdownListRow = memo<EmojiDropdownListRowProps>((props) => {
  // Find the row of data to render.
  const {index, style, listData} = props;
  const row = listData[index];

  // If this is a row of emojis, render it.
  if (!isEmojiCategoriesEnum(row)) {
    return renderRowOfEmoji(props, style, row);
  }

  // Otherwise, render the category title.
  return <StyledTitleDiv style={style}>{categoryRenderers[row]}</StyledTitleDiv>;
});

function renderRowOfEmoji(
  props: EmojiDropdownListRowProps,
  style: CSSProperties,
  row: ReadonlyArray<EmojiDefinition>,
) {
  // Render the row of emojis.
  const emojiRenders = row.map((emoji) => (
    <StyledEmojiDiv
      key={emoji.id}
      onMouseEnter={() => props.onEmojiMouseEnter(emoji)}
      onMouseLeave={() => props.onEmojiMouseLeave()}
      onClick={() => props.onEmojiClick(emoji)}
    >
      <Emoji useSheet emoji={emoji} skinTone={props.skinTone} size={emojiSize} />
    </StyledEmojiDiv>
  ));

  // Wrap and return.
  return <StyledRowDiv style={style}>{emojiRenders}</StyledRowDiv>;
}
